import React, { useEffect, useRef, useState } from "react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * QuoteImagePane
 * Horizontally-paged image viewer used in the split quotation layout.
 * - Swipe horizontally between pages via native scroll-snap.
 * - Each page is vertically scrollable inside its own area (native scroll).
 * - Pinch + double-tap zoom on each page via react-zoom-pan-pinch.
 *   Panning is disabled at scale=1 so scroll-snap + vertical scroll work smoothly.
 */
export function QuoteImagePane({ images = [], testid = "quote-image-pane" }) {
  const scrollerRef = useRef(null);
  const [page, setPage] = useState(0);

  const goTo = (idx) => {
    const el = scrollerRef.current;
    if (!el || !images.length) return;
    const clamped = Math.max(0, Math.min(images.length - 1, idx));
    el.scrollTo({ left: clamped * el.clientWidth, behavior: "smooth" });
    setPage(clamped);
  };

  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const idx = Math.round(el.scrollLeft / el.clientWidth);
    if (idx !== page) setPage(idx);
  };

  // Snap position back on window resize (keeps current page visible).
  useEffect(() => {
    const onResize = () => {
      const el = scrollerRef.current;
      if (!el) return;
      el.scrollTo({ left: page * el.clientWidth });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [page]);

  if (!images.length) return null;

  return (
    <div
      data-testid={testid}
      className="relative h-full w-full bg-slate-900 overflow-hidden"
    >
      <div
        ref={scrollerRef}
        onScroll={onScroll}
        data-testid={`${testid}-scroller`}
        className="h-full w-full flex overflow-x-auto overflow-y-hidden snap-x snap-mandatory scroll-smooth"
        style={{ scrollbarWidth: "none" }}
      >
        {images.map((src, i) => (
          <ImageSlide
            key={i}
            src={src}
            index={i}
            testid={`${testid}-slide-${i}`}
          />
        ))}
      </div>

      {images.length > 1 && (
        <>
          <button
            type="button"
            onClick={() => goTo(page - 1)}
            disabled={page === 0}
            data-testid={`${testid}-prev`}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 hover:bg-black/70 disabled:opacity-30 text-white grid place-items-center shadow-lg"
            aria-label="Previous page"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={() => goTo(page + 1)}
            disabled={page === images.length - 1}
            data-testid={`${testid}-next`}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 hover:bg-black/70 disabled:opacity-30 text-white grid place-items-center shadow-lg"
            aria-label="Next page"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </>
      )}

      <div
        data-testid={`${testid}-indicator`}
        className="absolute top-2 left-1/2 -translate-x-1/2 bg-black/60 text-white text-[11px] font-semibold rounded-full px-2.5 py-0.5 tabular-nums pointer-events-none"
      >
        {page + 1} / {images.length}
      </div>

      <div
        className="absolute bottom-2 left-1/2 -translate-x-1/2 text-[10px] text-white/70 pointer-events-none select-none"
        aria-hidden
      >
        Swipe · Double-tap or pinch to zoom
      </div>
    </div>
  );
}

/**
 * Single zoomable page. `panning.disabled=true` when scale==1 so that
 * scroll-snap swipes on the outer scroller and native vertical scroll
 * inside the slide are not consumed by the transform wrapper.
 */
function ImageSlide({ src, index, testid }) {
  const [scale, setScale] = useState(1);
  return (
    <div
      data-testid={testid}
      className="shrink-0 w-full h-full snap-start snap-always overflow-y-auto overflow-x-hidden bg-slate-900"
    >
      <TransformWrapper
        initialScale={1}
        minScale={1}
        maxScale={6}
        centerOnInit={false}
        doubleClick={{ mode: "toggle", step: 2 }}
        pinch={{ step: 5 }}
        wheel={{ step: 0.15 }}
        panning={{ disabled: scale <= 1.02, velocityDisabled: true }}
        limitToBounds={false}
        onTransformed={(_ref, state) => setScale(state.scale)}
      >
        <TransformComponent
          wrapperStyle={{ width: "100%", height: "auto" }}
          contentStyle={{ width: "100%", height: "auto" }}
        >
          <img
            src={src}
            alt={`Page ${index + 1}`}
            className="w-full h-auto block select-none"
            draggable={false}
          />
        </TransformComponent>
      </TransformWrapper>
    </div>
  );
}
